import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Grid,
  GridItem,
  Box,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  Button,
  SimpleGrid,
  Text,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Image,
  HStack,
  Link,
  Flex,
  useToast,
  Divider,
  Stack,
  Tooltip,
  Badge,
} from "@chakra-ui/react";
import { Dropdown } from "primereact/dropdown";
import { CustomerService } from "../../../../../core/Services";
import EmployeesSidebar from "../components/employees-sidebar/EmployeesSidebar";

/* =========================
   Helpers & Inline SVG
   ========================= */
const escapeHtml = (v = "") =>
  String(v)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");

const withProtocol = (url = "") => {
  const u = url.trim();
  if (!u) return "";
  if (/^(https?:)?\/\//i.test(u) || /^mailto:|^tel:/i.test(u)) return u;
  return `https://${u}`;
};

const displayUrl = (url = "") => url.replace(/^https?:\/\//i, "");

const svgIcon = (name, color = "#000000", size = 16) => {
  const fill = escapeHtml(color);
  const s = String(size);
  switch (name) {
    /* İletişim */
    case "user":
      return `<svg width="${s}" height="${s}" viewBox="0 0 24 24"><path fill="${fill}" d="M12 12a5 5 0 1 0-5-5 5 5 0 0 0 5 5zm0 2c-4.42 0-8 2.24-8 5v1h16v-1c0-2.76-3.58-5-8-5z"/></svg>`;
    case "briefcase":
      return `<svg width="${s}" height="${s}" viewBox="0 0 24 24"><path fill="${fill}" d="M10 4h4a2 2 0 0 1 2 2v2h3a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V10a2 2 0 0 1 2-2h3V6a2 2 0 0 1 2-2zm0 4h4V6h-4v2z"/></svg>`;
    case "phone":
      return `<svg width="${s}" height="${s}" viewBox="0 0 24 24"><path fill="${fill}" d="M6.62 10.79a15.053 15.053 0 0 0 6.59 6.59l2.2-2.2a1 1 0 0 1 1.01-.24c1.12.37 2.33.57 3.58.57a1 1 0 0 1 1 1V21a1 1 0 0 1-1 1C10.29 22 2 13.71 2 3a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.25.2 2.46.57 3.58a1 1 0 0 1-.24 1.01l-2.21 2.2z"/></svg>`;
    case "mobile":
      return `<svg width="${s}" height="${s}" viewBox="0 0 24 24"><path fill="${fill}" d="M7 2h10a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2zm5 18a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z"/></svg>`;
    case "email":
      return `<svg width="${s}" height="${s}" viewBox="0 0 24 24"><path fill="${fill}" d="M20 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2zm0 2-8 5-8-5h16zm0 12H4V8l8 5 8-5v10z"/></svg>`;
    case "web":
      return `<svg width="${s}" height="${s}" viewBox="0 0 24 24"><path fill="${fill}" d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm6.32 6H15.9a15.7 15.7 0 0 0-1.24-3.61A8.04 8.04 0 0 1 18.32 8zM12 4c.74 0 2.1 1.9 2.76 4H9.24C9.9 5.9 11.26 4 12 4zM6.68 8A8.04 8.04 0 0 1 9.34 4.39 15.7 15.7 0 0 0 8.1 8H6.68zM8.1 16a15.7 15.7 0 0 0 1.24 3.61A8.04 8.04 0 0 1 6.68 16H8.1zm1.14 0h5.52c-.66 2.1-2.02 4-2.76 4s-2.1-1.9-2.76-4zM15.9 16h2.42A8.04 8.04 0 0 1 14.66 19.61 15.7 15.7 0 0 0 15.9 16zM19 12c0 .68-.07 1.35-.2 2H5.2a8.15 8.15 0 0 1 0-4h13.6c.13.65.2 1.32.2 2z"/></svg>`;
    case "address":
      return `<svg width="${s}" height="${s}" viewBox="0 0 24 24"><path fill="${fill}" d="M12 2a7 7 0 0 0-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 0 0-7-7zm0 9.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5z"/></svg>`;
    /* Sosyal */
    case "whatsapp":
      return `<svg width="${s}" height="${s}" viewBox="0 0 24 24"><path fill="${fill}" d="M20.52 3.48A10 10 0 0 0 3 20.95L2 23l2.14-.56a10 10 0 0 0 4.3 1.02h.01A10 10 0 1 0 20.52 3.48zM12.46 19a7.5 7.5 0 0 1-3.81-1.04l-.27-.16-2.26.59.6-2.2-.18-.28A7.5 7.5 0 1 1 19 12.46 7.5 7.5 0 0 1 12.46 19zm3.66-5.55c-.2-.1-1.17-.58-1.35-.64-.18-.07-.3-.1-.43.1-.12.2-.5.64-.61.77-.11.13-.22.15-.42.05-.2-.1-.85-.31-1.62-.99-.6-.53-1-1.2-1.12-1.4-.12-.2 0-.31.09-.41.1-.1.2-.22.3-.33.1-.12.13-.2.2-.33.07-.13.03-.25-.02-.35-.05-.1-.43-1.04-.59-1.42-.16-.38-.32-.33-.43-.34-.11-.01-.24-.01-.37-.01s-.35.05-.53.25c-.18.2-.7.68-.7 1.66s.72 1.93.82 2.07c.1.13 1.42 2.17 3.44 3.05.48.21.85.33 1.14.42.48.15.92.13 1.27.08.39-.06 1.17-.48 1.34-.95.17-.47.17-.86.12-.95-.05-.09-.18-.14-.37-.24z"/></svg>`;
    case "facebook":
      return `<svg width="${s}" height="${s}" viewBox="0 0 24 24"><path fill="${fill}" d="M22 12a10 10 0 1 0-11.56 9.9v-7h-2.2V12h2.2V9.8c0-2.18 1.3-3.4 3.29-3.4.95 0 1.95.17 1.95.17v2.14h-1.1c-1.08 0-1.42.67-1.42 1.36V12h2.42l-.39 2.9h-2.03v7A10 10 0 0 0 22 12z"/></svg>`;
    case "instagram":
      return `<svg width="${s}" height="${s}" viewBox="0 0 24 24"><path fill="${fill}" d="M7 2h10a5 5 0 0 1 5 5v10a5 5 0 0 1-5 5H7a5 5 0 0 1-5-5V7a5 5 0 0 1 5-5zm0 2a3 3 0 0 0-3 3v10a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3V7a3 3 0 0 0-3-3H7zm5 3.5a5.5 5.5 0 1 1 0 11 5.5 5.5 0 0 1 0-11zm6.25-.75a1.25 1.25 0 1 1 0 2.5 1.25 1.25 0 0 1 0-2.5z"/></svg>`;
    case "linkedin":
      return `<svg width="${s}" height="${s}" viewBox="0 0 24 24"><path fill="${fill}" d="M4.98 3.5C4.98 4.88 3.86 6 2.5 6S0 4.88 0 3.5 1.12 1 2.5 1s2.48 1.12 2.48 2.5zM.5 8.5h4V23h-4V8.5zm7 0h3.83v1.98h.05c.53-.99 1.83-2.03 3.76-2.03 4.02 0 4.76 2.64 4.76 6.07V23h-4v-6.55c0-1.56-.03-3.58-2.18-3.58-2.18 0-2.52 1.7-2.52 3.46V23h-4V8.5z"/></svg>`;
    case "youtube":
      return `<svg width="${s}" height="${s}" viewBox="0 0 24 24"><path fill="${fill}" d="M23.5 6.2s-.23-1.65-.95-2.37c-.9-.95-1.9-.96-2.36-1.02C16.7 2.5 12 2.5 12 2.5s-4.7 0-8.19.31c-.46.06-1.46.07-2.36 1.02C.73 4.55.5 6.2.5 6.2S.25 8.1.25 10v1.99c0 1.9.25 3.8.25 3.8s.23 1.65.95 2.37c.9.95 2.08.92 2.61 1.03C6.12 19.5 12 19.5 12 19.5s4.7 0 8.19-.31c.46-.06 1.46-.07 2.36-1.02.72-.72.95-2.37.95-2.37s.25-1.9.25-3.8V10c0-1.9-.25-3.8-.25-3.8zM9.75 13.73V8.27L15.5 11l-5.75 2.73z"/></svg>`;
    case "x":
      return `<svg width="${s}" height="${s}" viewBox="0 0 24 24"><path fill="${fill}" d="M18.244 2H21.5l-7.5 8.57L22.5 22h-7.31l-5.72-6.62L3.5 22H.245l8.12-9.28L.5 2h7.31l5.18 6L18.244 2zm-2.57 18h2.03L8.36 4H6.33l9.344 16z"/></svg>`;
    default:
      return "";
  }
};

const InlineSvg = ({ name, color, size = 16 }) => (
  <span
    dangerouslySetInnerHTML={{ __html: svgIcon(name, color, size) }}
    style={{ display: "inline-flex", verticalAlign: "middle", lineHeight: 0 }}
  />
);

/* =========================
   Component
   ========================= */
export default function EmployeesSignature() {
  const toast = useToast();

  const [formData, setFormData] = useState({
    logoUrl: "",
    bannerUrl: "",
    font: "Arial",
    color: "#000000",
    iconColor: "#0a66c2", // ikon rengi
    iconSize: 16,         // ikon boyutu (bir tık büyük)
    width: "560px",
    name: "",
    title: "",
    phone: "",
    gsm: "",
    email: "",
    website: "www.ek.com.tr",
    company: "",
    address: "",
    info: "",
    whatsapp: "",
    facebook: "",
    instagram: "",
    linkedin: "",
    youtube: "",
    twitter: "",
  });

  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [uniqueCustomerId, setUniqueCustomerId] = useState("");
  const [error] = useState(false);

  useEffect(() => {
    CustomerService()
      .then((data) => setCustomers(data))
      .catch((err) => console.log("Müşteri Çekme Hatası", err));
  }, []);

  const handleChange = useCallback((e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]:
        e.target.type === "number" ? Number(e.target.value) : e.target.value,
    }));
  }, []);

  /* =========================
     HTML export (full)
     ========================= */
  const signatureFragment = useMemo(() => {
    const color = formData.color;
    const iconColor = formData.iconColor || color;
    const size = Number(formData.iconSize) || 16;

    const contactRows = [
      formData.phone && {
        icon: "phone",
        html: `<a href="tel:${escapeHtml(formData.phone)}" style="color:${escapeHtml(
          color
        )};text-decoration:none;">${escapeHtml(formData.phone)}</a>`,
      },
      formData.gsm && {
        icon: "mobile",
        html: `<a href="tel:${escapeHtml(formData.gsm)}" style="color:${escapeHtml(
          color
        )};text-decoration:none;">${escapeHtml(formData.gsm)}</a>`,
      },
      formData.email && {
        icon: "email",
        html: `<a href="mailto:${escapeHtml(formData.email)}" style="color:${escapeHtml(
          color
        )};text-decoration:none;">${escapeHtml(formData.email)}</a>`,
      },
      formData.website && {
        icon: "web",
        html: `<a href="${escapeHtml(
          withProtocol(formData.website)
        )}" target="_blank" style="color:${escapeHtml(
          color
        )};text-decoration:none;">${escapeHtml(displayUrl(formData.website))}</a>`,
      },
      formData.address && {
        icon: "address",
        html: `${escapeHtml(formData.address)}`,
      },
    ].filter(Boolean);

    const socials = [
      { key: "whatsapp", href: formData.whatsapp },
      { key: "facebook", href: formData.facebook },
      { key: "instagram", href: formData.instagram },
      { key: "linkedin", href: formData.linkedin },
      { key: "youtube", href: formData.youtube },
      { key: "x", href: formData.twitter },
    ].filter((s) => !!s.href);

    return `
<table cellpadding="0" cellspacing="0" border="0" style="max-width:${escapeHtml(
      formData.width
    )}; font-family:${escapeHtml(
      formData.font
    )}, Arial, sans-serif; color:${escapeHtml(color)};">
  <tr>
    <td valign="top" style="padding-right:12px; width:140px;">
      ${
        formData.logoUrl
          ? `<img src="${escapeHtml(
              formData.logoUrl
            )}" alt="Logo" style="max-width:120px; height:auto; display:block; margin-bottom:4px;" />`
          : ``
      }
    </td>
    <td valign="top" style="border-left:2px solid #D1D5DB; padding-left:12px;">
      <div style="display:flex; align-items:center; gap:6px; font-size:16px; font-weight:bold; line-height:1.3;">
        ${svgIcon("user", iconColor, size)}
        ${escapeHtml(formData.name)}
      </div>
      <div style="display:flex; align-items:center; gap:6px; font-size:12px; line-height:1.3; margin-top:2px;">
        ${svgIcon("briefcase", iconColor, size)}
        ${escapeHtml(formData.title)}
      </div>

      <table cellpadding="0" cellspacing="0" border="0" style="margin-top:8px;">
        ${contactRows
          .map(
            (row) => `
          <tr>
            <td valign="top" style="padding-right:8px; line-height:1.6;">
              ${svgIcon(row.icon, iconColor, size)}
            </td>
            <td valign="top" style="font-size:12px; line-height:1.6;">
              ${row.html}
            </td>
          </tr>`
          )
          .join("")}
        ${
          formData.company
            ? `<tr><td></td><td style="font-size:12px; line-height:1.6; padding-top:4px;">${escapeHtml(
                formData.company
              )}</td></tr>`
            : ``
        }
      </table>
    </td>
  </tr>

  ${
    socials.length
      ? `<tr>
      <td colspan="2" style="padding-top:10px;">
        <table cellpadding="0" cellspacing="0" border="0"><tr>
          ${socials
            .map(
              (s) => `
            <td style="padding-right:10px;">
              <a href="${escapeHtml(
                withProtocol(s.href)
              )}" target="_blank" style="text-decoration:none;">${svgIcon(
                s.key,
                iconColor,
                size
              )}</a>
            </td>`
            )
            .join("")}
        </tr></table>
      </td>
    </tr>`
      : ``
  }

  ${
    formData.bannerUrl
      ? `<tr>
      <td colspan="2" style="padding-top:10px;">
        <img src="${escapeHtml(
          formData.bannerUrl
        )}" alt="Banner" style="max-height:120px; width:100%; height:auto; display:block;" />
      </td>
    </tr>`
      : ``
  }
  ${
    formData.info
      ? `<tr><td colspan="2" style="padding-top:8px;">
      <div style="font-size:11px; line-height:1.4;">${escapeHtml(
        formData.info
      )}</div>
    </td></tr>`
      : ``
  }
</table>`.trim();
  }, [formData]);

  const wrapAsFullHtml = useCallback(
    (fragment) =>
      `<!doctype html>
<html lang="tr">
<head>
<meta charset="utf-8">
<meta http-equiv="x-ua-compatible" content="ie=edge">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${escapeHtml(formData.name || "signature")}</title>
</head>
<body>${fragment}</body>
</html>`,
    [formData.name]
  );

  const copyHtml = useCallback(() => {
    const htmlContent = wrapAsFullHtml(signatureFragment);
    if (window.ClipboardItem) {
      const data = {
        "text/html": new Blob([htmlContent], { type: "text/html;charset=utf-8" }),
        "text/plain": new Blob(
          [htmlContent.replace(/<[^>]+>/g, "")],
          { type: "text/plain;charset=utf-8" }
        ),
      };
      navigator.clipboard
        .write([new window.ClipboardItem(data)])
        .then(() =>
          toast({ title: "HTML panoya kopyalandı", status: "success", duration: 2000, isClosable: true })
        )
        .catch(() =>
          toast({ title: "Kopyalama başarısız", status: "error", duration: 2000, isClosable: true })
        );
    } else {
      navigator.clipboard
        .writeText(htmlContent)
        .then(() =>
          toast({ title: "HTML panoya kopyalandı (metin modu)", status: "warning", duration: 2500, isClosable: true })
        )
        .catch(() =>
          toast({ title: "Kopyalama başarısız", status: "error", duration: 2000, isClosable: true })
        );
    }
  }, [signatureFragment, toast, wrapAsFullHtml]);

  const downloadHtml = useCallback(() => {
    const htmlContent = wrapAsFullHtml(signatureFragment);
    const blob = new Blob([htmlContent], { type: "text/html;charset=utf-8" });
    const link = document.createElement("a");
    const baseName = (formData.name || "signature").trim().replace(/\s+/g, "-").toLowerCase();
    link.href = URL.createObjectURL(blob);
    link.download = `${baseName}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [signatureFragment, formData.name, wrapAsFullHtml]);

  /* =========================
     UI
     ========================= */
  const ContactRow = ({ icon, children }) => (
    <HStack align="flex-start" spacing={2}>
      <InlineSvg name={icon} color={formData.iconColor || formData.color} size={formData.iconSize} />
      <Text fontSize="sm">{children}</Text>
    </HStack>
  );

  const SocialIconsRow = () => {
    const iconColor = formData.iconColor || formData.color;
    const items = [
      { key: "whatsapp", href: formData.whatsapp },
      { key: "facebook", href: formData.facebook },
      { key: "instagram", href: formData.instagram },
      { key: "linkedin", href: formData.linkedin },
      { key: "youtube", href: formData.youtube },
      { key: "x", href: formData.twitter },
    ].filter((i) => !!i.href);

    if (!items.length) return null;

    return (
      <HStack spacing={4} mt={4}>
        {items.map((i) => (
          <Link key={i.key} href={withProtocol(i.href)} target="_blank">
            <InlineSvg name={i.key} color={iconColor} size={formData.iconSize} />
          </Link>
        ))}
      </HStack>
    );
  };

  const shortUid = uniqueCustomerId ? `${uniqueCustomerId.substring(0, 8)}…` : "";

  return (
    <Grid
      templateAreas={`"nav main"`}
      gridTemplateRows={"auto 1fr"}
      gridTemplateColumns={"auto 1fr"}
      h="100vh"
      overflow="hidden"
    >
      <GridItem area={"nav"} borderRight="1px solid" borderColor="gray.100">
        <EmployeesSidebar />
      </GridItem>

      <GridItem area={"main"} p={5} overflowY="auto">
        {/* Üst bar: müşteri seçimi */}
        <Flex
          align="center"
          justify="center"
          wrap="wrap"
          w="100%"
          mb={6}
          p={4}
          bg="rgba(237, 237, 237, 0.8)"
          color="black"
          borderRadius="lg"
        >
          <HStack>
            <Dropdown
              showClear
              filter
              onChange={(e) => {
                const selectedId = e.value;
                const selected = customers.find((c) => c.id === selectedId);
                setSelectedCustomer(selectedId);
                setUniqueCustomerId(selected?.unique_id || "");
              }}
              options={
                customers?.length > 0
                  ? customers.map((c) => ({ label: c.title, value: c.id }))
                  : []
              }
              value={selectedCustomer}
              placeholder="Müşteri Adı / Ünvanı"
              style={{
                border: `2px solid ${error && !selectedCustomer ? "red" : "transparent"}`,
                borderRadius: "1rem",
                width: "22rem",
              }}
            />
            {uniqueCustomerId ? (
              <Tooltip label={uniqueCustomerId}>
                <Badge colorScheme="green">UID: {shortUid}</Badge>
              </Tooltip>
            ) : (
              <Badge colorScheme="gray">Müşteri seçiniz</Badge>
            )}
          </HStack>
        </Flex>

        {/* Başlık + aksiyonlar */}
        <HStack justify="space-between" align="center" mb={3}>
          <Text fontSize="2xl" fontWeight="bold">
            Önizleme
          </Text>
          <HStack spacing={3}>
            <Tooltip label="HTML içeriğini panoya kopyala (ikonlar korunur)">
              <Button size="sm" colorScheme="blue" variant="outline" onClick={copyHtml}>
                HTML Kopyala
              </Button>
            </Tooltip>
            <Tooltip label="HTML dosyası olarak indir (UTF-8)">
              <Button size="sm" colorScheme="green" variant="outline" onClick={downloadHtml}>
                HTML İndir
              </Button>
            </Tooltip>
          </HStack>
        </HStack>

        {/* Önizleme */}
        <Box
          id="signature-preview"
          border="1px solid #ddd"
          borderRadius="md"
          bg="white"
          p={5}
          mb={8}
          maxW={formData.width}
          mx="auto"
          fontFamily={formData.font}
          color={formData.color}
        >
          <Grid templateColumns="140px 1fr" gap={4} alignItems="flex-start">
            <Box>
              {formData.logoUrl ? (
                <Image src={formData.logoUrl} alt="Logo" maxW="120px" mb={2} />
              ) : (
                <Box
                  w="120px"
                  h="60px"
                  border="1px dashed"
                  borderColor="gray.300"
                  borderRadius="md"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  fontSize="xs"
                  color="gray.400"
                >
                  Logo
                </Box>
              )}
            </Box>

            <Box borderLeft="2px solid #D1D5DB" pl={4}>
              <HStack spacing={2} align="center">
                <InlineSvg name="user" color={formData.iconColor || formData.color} size={formData.iconSize} />
                <Text fontSize="lg" fontWeight="bold">
                  {formData.name || "Ad Soyad"}
                </Text>
              </HStack>
              <HStack spacing={2} align="center">
                <InlineSvg name="briefcase" color={formData.iconColor || formData.color} size={formData.iconSize} />
                <Text fontSize="sm" color="gray.700">
                  {formData.title || "Ünvan"}
                </Text>
              </HStack>

              <Stack spacing={1.5} mt={3}>
                {formData.phone && (
                  <ContactRow icon="phone">
                    <Link href={`tel:${formData.phone}`} color={formData.color}>
                      {formData.phone}
                    </Link>
                  </ContactRow>
                )}
                {formData.gsm && (
                  <ContactRow icon="mobile">
                    <Link href={`tel:${formData.gsm}`} color={formData.color}>
                      {formData.gsm}
                    </Link>
                  </ContactRow>
                )}
                {formData.email && (
                  <ContactRow icon="email">
                    <Link href={`mailto:${formData.email}`} color={formData.color}>
                      {formData.email}
                    </Link>
                  </ContactRow>
                )}
                {formData.website && (
                  <ContactRow icon="web">
                    <Link href={withProtocol(formData.website)} target="_blank" color={formData.color}>
                      {displayUrl(formData.website)}
                    </Link>
                  </ContactRow>
                )}
                {formData.address && (
                  <ContactRow icon="address">{formData.address}</ContactRow>
                )}
                {formData.company && (
                  <Text fontSize="sm" mt={1}>
                    {formData.company}
                  </Text>
                )}
              </Stack>
            </Box>
          </Grid>

          <SocialIconsRow />

          {formData.bannerUrl && (
            <Image
              src={formData.bannerUrl}
              alt="Banner"
              maxH="120px"
              w="100%"
              mt={3}
              borderRadius="md"
              objectFit="cover"
            />
          )}

          {formData.info && (
            <Text fontSize="xs" mt={2} color="gray.700">
              {formData.info}
            </Text>
          )}
        </Box>

        {/* Form */}
        <Text fontSize="2xl" mb={4} fontWeight="bold">
          Bilgiler
        </Text>

        <Accordion allowToggle defaultIndex={[0]}>
          {/* Genel */}
          <AccordionItem>
            <AccordionButton>
              <Box flex="1" textAlign="left" fontWeight="bold">
                Genel
              </Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel>
              <SimpleGrid columns={[1, 2]} spacing={4}>
                <FormControl>
                  <FormLabel>Logo URL</FormLabel>
                  <Input
                    name="logoUrl"
                    value={formData.logoUrl}
                    onChange={handleChange}
                    placeholder="https://.../logo.png"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Banner URL</FormLabel>
                  <Input
                    name="bannerUrl"
                    value={formData.bannerUrl}
                    onChange={handleChange}
                    placeholder="https://.../banner.jpg"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Yazı Fontu</FormLabel>
                  <Select name="font" value={formData.font} onChange={handleChange}>
                    <option value="Arial">Arial</option>
                    <option value="Segoe UI">Segoe UI</option>
                    <option value="Roboto">Roboto</option>
                    <option value="Times New Roman">Times New Roman</option>
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel>Ana Renk (HEX)</FormLabel>
                  <Input type="color" name="color" value={formData.color} onChange={handleChange} />
                </FormControl>

                <FormControl>
                  <FormLabel>İkon Rengi (HEX)</FormLabel>
                  <Input type="color" name="iconColor" value={formData.iconColor} onChange={handleChange} />
                </FormControl>

                <FormControl>
                  <FormLabel>İmza Genişliği</FormLabel>
                  <Select name="width" value={formData.width} onChange={handleChange}>
                    <option value="560px">560px</option>
                    <option value="768px">768px</option>
                    <option value="916px">916px</option>
                    <option value="1024px">1024px</option>
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel>İkon Boyutu</FormLabel>
                  <Select name="iconSize" value={formData.iconSize} onChange={handleChange}>
                    <option value={14}>14</option>
                    <option value={16}>16</option>
                    <option value={18}>18</option>
                    <option value={20}>20</option>
                  </Select>
                </FormControl>
              </SimpleGrid>
            </AccordionPanel>
          </AccordionItem>

          {/* Kişisel Bilgi */}
          <AccordionItem>
            <AccordionButton>
              <Box flex="1" textAlign="left" fontWeight="bold">
                Kişisel Bilgi
              </Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel>
              <SimpleGrid columns={[1, 2]} spacing={4}>
                <FormControl>
                  <FormLabel>Ad Soyad</FormLabel>
                  <Input name="name" value={formData.name} onChange={handleChange} placeholder="Ad Soyad" />
                </FormControl>
                <FormControl>
                  <FormLabel>Ünvan</FormLabel>
                  <Input name="title" value={formData.title} onChange={handleChange} placeholder="Ünvan" />
                </FormControl>
              </SimpleGrid>
            </AccordionPanel>
          </AccordionItem>

          {/* İletişim */}
          <AccordionItem>
            <AccordionButton>
              <Box flex="1" textAlign="left" fontWeight="bold">
                İletişim
              </Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel>
              <SimpleGrid columns={[1, 2]} spacing={4}>
                <FormControl>
                  <FormLabel>Telefon</FormLabel>
                  <Input name="phone" value={formData.phone} onChange={handleChange} placeholder="+90 212 000 00 00" />
                </FormControl>
                <FormControl>
                  <FormLabel>GSM</FormLabel>
                  <Input name="gsm" value={formData.gsm} onChange={handleChange} placeholder="+90 5xx xxx xx xx" />
                </FormControl>
                <FormControl>
                  <FormLabel>E-posta</FormLabel>
                  <Input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="ad.soyad@sirket.com" />
                </FormControl>
                <FormControl>
                  <FormLabel>Web Sitesi</FormLabel>
                  <Input name="website" value={formData.website} onChange={handleChange} placeholder="www.sirket.com" />
                </FormControl>
                <FormControl gridColumn={{ md: "1 / -1" }}>
                  <FormLabel>Adres</FormLabel>
                  <Textarea name="address" value={formData.address} onChange={handleChange} placeholder="Adres" />
                </FormControl>
                <FormControl>
                  <FormLabel>Firma Adı</FormLabel>
                  <Input name="company" value={formData.company} onChange={handleChange} placeholder="Firma Adı" />
                </FormControl>
              </SimpleGrid>
            </AccordionPanel>
          </AccordionItem>

          {/* Bilgi */}
          <AccordionItem>
            <AccordionButton>
              <Box flex="1" textAlign="left" fontWeight="bold">
                Bilgi Alanı
              </Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel>
              <FormControl>
                <FormLabel>Bilgi / KVKK Metni</FormLabel>
                <Textarea name="info" value={formData.info} onChange={handleChange} placeholder="KVKK, açık rıza, uyarı metinleri..." />
              </FormControl>
            </AccordionPanel>
          </AccordionItem>

          {/* Sosyal Medya */}
          <AccordionItem>
            <AccordionButton>
              <Box flex="1" textAlign="left" fontWeight="bold">
                Sosyal Medya
              </Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel>
              <SimpleGrid columns={[1, 2]} spacing={4}>
                <FormControl>
                  <FormLabel>WhatsApp</FormLabel>
                  <Input name="whatsapp" value={formData.whatsapp} onChange={handleChange} placeholder="wa.me/90..." />
                </FormControl>
                <FormControl>
                  <FormLabel>Facebook</FormLabel>
                  <Input name="facebook" value={formData.facebook} onChange={handleChange} placeholder="facebook.com/..." />
                </FormControl>
                <FormControl>
                  <FormLabel>Instagram</FormLabel>
                  <Input name="instagram" value={formData.instagram} onChange={handleChange} placeholder="instagram.com/..." />
                </FormControl>
                <FormControl>
                  <FormLabel>LinkedIn</FormLabel>
                  <Input name="linkedin" value={formData.linkedin} onChange={handleChange} placeholder="linkedin.com/in/... veya /company/..." />
                </FormControl>
                <FormControl>
                  <FormLabel>YouTube</FormLabel>
                  <Input name="youtube" value={formData.youtube} onChange={handleChange} placeholder="youtube.com/@..." />
                </FormControl>
                <FormControl>
                  <FormLabel>X (Twitter)</FormLabel>
                  <Input name="twitter" value={formData.twitter} onChange={handleChange} placeholder="x.com/..." />
                </FormControl>
              </SimpleGrid>
            </AccordionPanel>
          </AccordionItem>
        </Accordion>

        {/* Alt aksiyonlar */}
        <Stack direction={{ base: "column", md: "row" }} spacing={4} mt={8}>
          <Button colorScheme="blue" variant="outline" onClick={copyHtml}>
            HTML Kopyala
          </Button>
          <Button colorScheme="green" variant="outline" onClick={downloadHtml}>
            HTML İndir
          </Button>
          <Divider orientation="vertical" display={{ base: "none", md: "block" }} />
          <Tooltip label="Faz-3: Veritabanına kaydetme eklenecek">
            <Button isDisabled variant="outline">
              Kaydet (DB)
            </Button>
          </Tooltip>
          <Tooltip label="Faz-3: Veritabanından silme eklenecek">
            <Button isDisabled colorScheme="red" variant="outline">
              Sil (DB)
            </Button>
          </Tooltip>
        </Stack>
      </GridItem>
    </Grid>
  );
}
